import React, { useState, useEffect } from 'react';
import { Search, Package, ArrowDownCircle, ArrowUpCircle, Loader2, XCircle, AlertTriangle, User, Warehouse, RefreshCw } from 'lucide-react';
import { getInventory, consumeInventory, restockInventory, getWarehouses } from './api';
import { useLanguage } from './LanguageContext';

export default function InventoryScreen({ onGoToProfile }) {
    const { t } = useLanguage();

    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedItem, setSelectedItem] = useState(null);
    const [actionType, setActionType] = useState(null); // 'consume' or 'restock'
    const [quantity, setQuantity] = useState('');
    const [remarks, setRemarks] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedWarehouseId !== '') {
            fetchInventory(selectedWarehouseId);
        } else if (warehouses.length > 0) {
            // If no warehouse selected but we have warehouses, maybe select first?
            // Or wait for user. Let's fetch all (no filter) or wait.
            fetchInventory();
        }
    }, [selectedWarehouseId]);

    const fetchInitialData = async () => {
        try {
            const wRes = await getWarehouses();
            setWarehouses(wRes.data);
            if (wRes.data.length > 0) {
                setSelectedWarehouseId(wRes.data[0].id);
            } else {
                fetchInventory();
            }
        } catch (error) {
            console.error("Failed to fetch warehouses", error);
            fetchInventory(); // Fallback to fetching all inventory
        }
    };

    const fetchInventory = async (warehouseId = null) => {
        setLoading(true);
        try {
            const res = await getInventory(warehouseId);
            setItems(res.data);
        } catch (error) {
            console.error("Failed to fetch inventory", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleAction = (item, type) => {
        setSelectedItem(item);
        setActionType(type);
        setQuantity('');
        setRemarks('');
        setShowConfirmModal(false);
    };

    const handlePreSubmit = (e) => {
        e.preventDefault();
        if (!quantity || isNaN(quantity) || parseFloat(quantity) <= 0) {
            alert(t('inventory.invalidQuantity'));
            return;
        }
        setShowConfirmModal(true);
    };

    const handleFinalSubmit = async () => {
        setSubmitting(true);
        try {
            if (actionType === 'consume') {
                await consumeInventory(selectedItem.id, quantity, remarks);
            } else {
                await restockInventory(selectedItem.id, quantity, remarks);
            }

            // Update local state
            await fetchInventory(selectedWarehouseId || null);
            setSelectedItem(null);
            setActionType(null);
            setShowConfirmModal(false);
        } catch (error) {
            console.error("Failed to update inventory", error);
            alert(t('inventory.operationFailed') + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const getSelectedWarehouseName = () => {
        const w = warehouses.find(wh => String(wh.id) === String(selectedWarehouseId));
        return w ? w.name : t('inventory.unknownWarehouse');
    };

    if (loading && items.length === 0) {
        return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>;
    }

    return (
        <div className="p-4 pb-24 md:p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('inventory.title')}</h1>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => fetchInventory(selectedWarehouseId || null)}
                        disabled={loading}
                        className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:text-indigo-600 transition-colors disabled:opacity-50"
                        title={t('inventory.refreshTitle')}
                    >
                        <RefreshCw size={24} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button onClick={onGoToProfile} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:text-indigo-600 transition-colors">
                        <User size={24} />
                    </button>
                </div>
            </div>

            <div className="mb-4">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Warehouse size={18} className="text-gray-400" />
                    </div>
                    <select
                        value={selectedWarehouseId}
                        onChange={(e) => setSelectedWarehouseId(e.target.value)}
                        className="pl-10 block w-full rounded-lg border-gray-300 bg-white border focus:border-indigo-500 focus:ring-indigo-500 p-3 shadow-sm appearance-none"
                    >
                        {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ArrowDownCircle size={16} className="text-gray-400" />
                    </div>
                </div>
            </div>

            <div className="relative mb-6">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={20} className="text-gray-400" />
                </div>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 block w-full rounded-lg border-gray-300 bg-white border focus:border-indigo-500 focus:ring-indigo-500 p-3 shadow-sm"
                    placeholder={t('inventory.searchPlaceholder')}
                />
            </div>

            <div className="grid grid-cols-1 gap-4">
                {filteredItems.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <div className="flex items-start mb-3 sm:mb-0">
                            <div className={`p-2 rounded-full mr-3 flex-shrink-0 ${item.quantity <= item.safety_stock ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
                                <Package size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-800 text-lg">{item.name}</h3>
                                <div className="flex items-center mt-1">
                                    <span className={`font-mono font-bold text-lg ${item.quantity <= item.safety_stock ? 'text-red-600' : 'text-gray-600'}`}>
                                        {parseFloat(item.quantity)}
                                    </span>
                                    {item.quantity <= item.safety_stock && (
                                        <span className="ml-2 flex items-center text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">
                                            <AlertTriangle size={12} className="mr-1" /> {t('inventory.lowStock')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex space-x-2 w-full sm:w-auto">
                            <button
                                onClick={() => handleAction(item, 'consume')}
                                className="flex-1 sm:flex-none flex items-center justify-center bg-white border border-red-200 text-red-600 hover:bg-red-50 py-2 px-3 rounded-md transition-colors text-sm font-medium"
                            >
                                <ArrowUpCircle size={16} className="mr-1.5" />
                                {t('inventory.consume')}
                            </button>
                            <button
                                onClick={() => handleAction(item, 'restock')}
                                className="flex-1 sm:flex-none flex items-center justify-center bg-white border border-green-200 text-green-600 hover:bg-green-50 py-2 px-3 rounded-md transition-colors text-sm font-medium"
                            >
                                <ArrowDownCircle size={16} className="mr-1.5" />
                                {t('inventory.restock')}
                            </button>
                        </div>
                    </div>
                ))}

                {filteredItems.length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                        <Package size={48} className="mx-auto text-gray-300 mb-2" />
                        <p>{t('inventory.noItems')}</p>
                    </div>
                )}
            </div>

            {selectedItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl animate-in zoom-in-95 duration-200" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800">
                                {actionType === 'consume' ? t('inventory.consumeTitle') : t('inventory.restockTitle')} - {selectedItem.name}
                            </h3>
                            <button
                                onClick={() => {
                                    setSelectedItem(null);
                                    setShowConfirmModal(false);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle size={24} />
                            </button>
                        </div>

                        {!showConfirmModal ? (
                            <form onSubmit={handlePreSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('inventory.quantity')} <span className="text-gray-500 font-normal">({t('inventory.currentQuantity')}: {parseFloat(selectedItem.quantity)})</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 p-2.5 text-lg font-mono"
                                        placeholder="0.00"
                                        autoFocus
                                        required
                                    />
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('inventory.remarksOptional')}</label>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 p-2.5"
                                        rows="2"
                                        placeholder={t('inventory.remarksPlaceholder')}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className={`w-full py-3 px-4 rounded-lg shadow-md text-white font-bold transition-colors flex justify-center items-center ${actionType === 'consume'
                                            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                            : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                        }`}
                                >
                                    {t('inventory.next')}
                                </button>
                            </form>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('inventory.warehouse')}:</span>
                                        <span className="font-medium text-gray-900">{getSelectedWarehouseName()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('inventory.item')}:</span>
                                        <span className="font-medium text-gray-900">{selectedItem.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('inventory.quantity')}:</span>
                                        <span className={`font-bold font-mono text-base ${actionType === 'consume' ? 'text-red-600' : 'text-green-600'}`}>
                                            {actionType === 'consume' ? '-' : '+'}{quantity}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('inventory.remarks')}:</span>
                                        <span className="font-medium text-gray-900 text-right max-w-[150px] truncate">{remarks || '-'}</span>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-2">
                                    <button
                                        onClick={() => setShowConfirmModal(false)}
                                        className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                    >
                                        {t('inventory.backToEdit')}
                                    </button>
                                    <button
                                        onClick={handleFinalSubmit}
                                        disabled={submitting}
                                        className={`flex-1 py-2 px-4 rounded-lg shadow-md text-white font-bold transition-colors flex justify-center items-center ${actionType === 'consume'
                                                ? 'bg-red-600 hover:bg-red-700 ring-offset-2 focus:ring-2 focus:ring-red-500'
                                                : 'bg-green-600 hover:bg-green-700 ring-offset-2 focus:ring-2 focus:ring-green-500'
                                            } ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting && <Loader2 className="animate-spin mr-2" size={18} />}
                                        {actionType === 'consume' ? t('inventory.confirmConsume') : t('inventory.confirmRestock')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
